import { observable, toJS } from 'mobx';

import { SerializedData, Store, RouteStore, FetchOptions, QueryCombiner, ContextStore } from 'bernie-core';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { FlightsStandardOffer } from '__generated__/api';
import { SearchQueryAdapter } from 'stores/adapters';
import {
  cabinClassConverter,
  generateDeeplink,
  createSearchParameters,
  generateSearchQuery,
  sanitizeFilterValues
} from 'utility';
import { SortFilterStore, SearchFormStore, ListingsStore } from '.';

import {
  FlightsCabinClass,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsFlexibleSearch,
  FlightsTravelerType,
  FlightsTripType,
  FlightsQueryState,
  FlightsJourneyCriteriaInput,
  FlightsSearchFilterValuesInput,
  FlightsFlexSearchResults,
  FlightsSortOptionTypeInput,
  FlightsSortOrder,
  FlightsSortType,
  FlightAction
} from '__generated__/api/types';

import {
  FlightsSearchFormType,
  FlightsSearchFormJourneyCriteriaType,
  FlightsSearchFormTripTypeOptionItem,
  FlightsSearchFormTravelersOption,
  FlightsSearchFormMoreFiltersOption,
  FlightsSearchFormAirlineOption,
  FlightsSearchFormCabinClassOption,
  TravelerOptionItem,
  FlightsTravelerAgeSelector
} from 'types/search/wizard';
import { FlightsFilterLoadedOptionType, AppliedFiltersType } from 'types/search/sort-filter';
import { FlightsFilterItem } from 'types/shared';

import NextSearchCriteria = FlightsFlexSearchResults.NextSearchCriteria;
import ListingsAction = FlightsShoppingPwaFlightSearchResultsFlightsSearch.ListingsAction;
import SearchPagination = FlightAction.SearchPagination;

export class SearchCriteriaStore extends Store {
  @observable public searchCriteria: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;
  @observable public flexSearchCriteria: FlightsFlexibleSearch.Variables;
  @observable public forceFetch: boolean;
  @observable public fetchFlexResults: boolean;
  public initialTripType: FlightsTripType;
  public pageId: string;

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
    this.initialTripType = this.searchCriteria.flightsSearchContext.tripType;
  }

  public cleanUpSortFilterOption() {
    this.searchCriteria.sortOption = null;
    this.searchCriteria.searchFilterValuesList = [];
  }

  private areFiltersCleanedUp(filters: string) {
    if (!filters) {
      if (this.searchCriteria.searchFilterValuesList.length > 0) {
        this.searchCriteria.searchFilterValuesList = [];
        this.forceFetch = true;
      }

      return true;
    }

    return false;
  }

  private urlHasSameNumberOfFilters(filtersFromUrl: FlightsSearchFilterValuesInput[]) {
    return filtersFromUrl.length === this.searchCriteria.searchFilterValuesList.length;
  }

  private areSameFiltersApplied(filtersFromUrl: FlightsSearchFilterValuesInput[]) {
    let urlHasSameFilters = true;

    filtersFromUrl.forEach(filterInUrl => {
      const keyForFilterInUrl = Object.keys(filterInUrl)[0];
      const keyForFilterValueFromUrl = Object.keys(filterInUrl[keyForFilterInUrl])[0];
      const filterValueFromUrl = filterInUrl[keyForFilterInUrl][keyForFilterValueFromUrl];
      let filterFound = false;

      this.searchCriteria.searchFilterValuesList.forEach(filterValueList => {
        const keyForFilterInCriteria = Object.keys(filterValueList)[0];
        const keyForFilterValueFromCriteria = Object.keys(filterValueList[keyForFilterInCriteria])[0];
        const filterValueFromCriteria = filterValueList[keyForFilterInCriteria][keyForFilterValueFromCriteria];

        if (filterFound) {
          return;
        }

        if (
          keyForFilterInUrl !== keyForFilterInCriteria ||
          keyForFilterValueFromCriteria !== keyForFilterValueFromUrl ||
          typeof filterValueFromCriteria !== typeof filterValueFromUrl
        ) {
          return;
        }

        if (typeof filterValueFromCriteria === 'string' && filterValueFromCriteria === filterValueFromUrl) {
          filterFound = true;

          return;
        }

        if (typeof filterValueFromCriteria === 'object') {
          const keyForNestedFilterValueOperationFromUrl = Object.keys(filterValueFromUrl)[0];
          const keyForNestedFilterValueOperationFromCriteria = Object.keys(filterValueFromCriteria)[0];
          const keyForNestedFilterValueFromUrl = Object.keys(filterValueFromUrl)[1];
          const keyForNestedFilterValueFromCriteria = Object.keys(filterValueFromCriteria)[1];

          const nestedFilterValueOperationFromUrl = filterValueFromUrl[keyForNestedFilterValueOperationFromUrl];
          const nestedFilterValueOperationFromCriteria =
            filterValueFromCriteria[keyForNestedFilterValueOperationFromCriteria];
          const nestedFilterValueFromUrl = filterValueFromUrl[keyForNestedFilterValueFromUrl];
          const nestedFilterValueFromCriteria = filterValueFromCriteria[keyForNestedFilterValueFromCriteria];

          if (
            nestedFilterValueOperationFromUrl === nestedFilterValueOperationFromCriteria &&
            nestedFilterValueFromUrl === nestedFilterValueFromCriteria
          ) {
            filterFound = true;
          }
        }
      });

      if (!filterFound) {
        urlHasSameFilters = false;
      }
    });

    return urlHasSameFilters;
  }

  public syncFiltersWithCriteriaFromUrl(filters: string): void {
    let filtersFromUrl = [];

    //TODO: Improve all this logic by having filter IDENTIFIERS https://jira.expedia.biz/browse/FODF-857

    if (this.areFiltersCleanedUp(filters)) {
      return;
    }

    try {
      filtersFromUrl = JSON.parse(filters);
    } catch (error) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);

      return;
    }

    if (this.urlHasSameNumberOfFilters(filtersFromUrl) && this.areSameFiltersApplied(filtersFromUrl)) {
      return;
    }

    this.searchCriteria.searchFilterValuesList = filtersFromUrl;
    this.forceFetch = true;
  }

  private isSameSortOption(sortOptionInUrl: FlightsSortOptionTypeInput) {
    const sortOptionInCriteria = this.searchCriteria.sortOption;

    return (
      sortOptionInCriteria &&
      sortOptionInCriteria.sortOrder === sortOptionInUrl.sortOrder &&
      sortOptionInCriteria.sortType === sortOptionInUrl.sortType
    );
  }

  private isValidSortOption(sortOrder: string, sortType: string) {
    if (FlightsSortOrder[sortOrder] && FlightsSortType[sortType]) {
      return true;
    }

    return false;
  }

  public syncSortWithCriteriaFromUrl(sortOrder: string, sortType: string): boolean {
    if (!sortType || !sortOrder) {
      return;
    }

    if (!this.isValidSortOption(sortOrder, sortType)) {
      this.searchCriteria.sortOption = null;
      this.forceFetch = true;

      return;
    }

    const sortOptionInUrl: FlightsSortOptionTypeInput = {
      sortOrder: FlightsSortOrder[sortOrder],
      sortType: FlightsSortType[sortType]
    };

    if (this.isSameSortOption(sortOptionInUrl)) {
      return;
    }

    this.searchCriteria.sortOption = sortOptionInUrl;
    this.forceFetch = true;
  }

  public setCriteriaFromUrl(fetchOptions: FetchOptions) {
    const query = fetchOptions.query;
    const options = (query && query.options && query.options.split(',')) || [];
    const journey =
      query.trip === 'oneway'
        ? Object.keys(query).filter(name => name === 'leg1' && /leg/.test(name))
        : Object.keys(query).filter(name => /leg/.test(name));
    const passengers = (query && query.passengers && query.passengers.split(',')) || [];
    const localDateFormat = fetchOptions.request.context.localDateFormat;
    const locale = fetchOptions.request.context.locale;
    const journeysContinuationId = query && query.journeysContinuationId;
    this.pageId = (query && query.pageId) || 0;

    this.syncFiltersWithCriteriaFromUrl(query.filters);
    this.syncSortWithCriteriaFromUrl(query.sortOrder, query.sortType);

    this.setJourneysContinuationId(journeysContinuationId);

    this.searchCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(query.trip);

    this.flexSearchCriteria.flightsSearchContext.tripType = SearchQueryAdapter.mapTripType(query.trip);

    this.searchCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(options);

    this.flexSearchCriteria.searchPreferences.advancedFilters = SearchQueryAdapter.mapAdvancedFilters(options);

    this.searchCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(
      options.find(option => option.includes('carrier'))
    );

    this.flexSearchCriteria.searchPreferences.airline = SearchQueryAdapter.mapAirline(
      options.find(option => option.includes('carrier'))
    );

    this.searchCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(
      options.find(option => option.includes('cabinclass'))
    );

    this.flexSearchCriteria.searchPreferences.cabinClass = SearchQueryAdapter.mapCabinClass(
      options.find(option => option.includes('cabinclass'))
    );

    this.searchCriteria.travelerDetails = SearchQueryAdapter.mapTravelers(passengers);

    this.flexSearchCriteria.travelerDetails = SearchQueryAdapter.mapTravelers(passengers);

    this.searchCriteria.journeyCriteria = journey.map(leg =>
      SearchQueryAdapter.mapLeg(query[leg], locale, localDateFormat)
    );

    this.flexSearchCriteria.journeyCriteria = journey.map(leg =>
      SearchQueryAdapter.mapLeg(query[leg], locale, localDateFormat)
    );
  }

  public triggerSearch(
    routeStore: RouteStore,
    context?: ContextStore,
    shouldForce?: boolean,
    replaceHistory?: boolean,
    fetchFlexResults = true
  ) {
    const { initialTripType, searchCriteria } = this;
    const tripTypeChanged = initialTripType && initialTripType !== searchCriteria.flightsSearchContext.tripType;
    const searchParameters = createSearchParameters(searchCriteria);
    const deeplink = generateDeeplink(searchParameters, context, this.pageId);
    const queryCombiner: QueryCombiner = (prevQuery, newQuery) => newQuery;

    if (shouldForce) {
      this.forceFetch = shouldForce;
    }

    this.fetchFlexResults = fetchFlexResults;

    // TODO: Update to always do `updateSearch` once we don't depend on CGP for experiment evaluation
    if (tripTypeChanged) {
      window.location.search = generateSearchQuery(deeplink);
    } else {
      routeStore.updateSearch(deeplink, undefined, replaceHistory, queryCombiner);
    }
  }

  public syncWithNextSearchCriteria(nextSearchCriteria: NextSearchCriteria) {
    const { journeyCriteria, searchPreferences } = nextSearchCriteria;
    this.searchCriteria.journeyCriteria[0].departureDate = journeyCriteria[0].departureDate;
    if (journeyCriteria.length === 2 && this.searchCriteria.journeyCriteria[1]) {
      this.searchCriteria.journeyCriteria[1].departureDate = journeyCriteria[1].departureDate;
    }
    this.searchCriteria.searchPreferences.cabinClass = cabinClassConverter(searchPreferences.cabinClass);
  }

  public setJourneysContinuationId(journeysContinuationId: string) {
    this.searchCriteria.flightsSearchContext.journeysContinuationId =
      (journeysContinuationId !== 'null' && journeysContinuationId) || null;
  }

  public syncWithSortAndFilterStore(sortAndFilterStore: SortFilterStore) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);

    Object.assign(searchCriteriaFeedback, this.getSortOptionFromSortAndFilterResult(sortAndFilterStore));
    Object.assign(
      searchCriteriaFeedback,
      sortAndFilterStore.filterPresentation &&
        sortAndFilterStore.filterPresentation.options &&
        this.getSearchFilterValuesListFromSortAndFilterResult(sortAndFilterStore)
    );
    Object.assign(searchCriteriaFeedback, this.unsetPagination());

    this.searchCriteria = searchCriteriaFeedback;
  }

  public syncWithAppliedSortAndFilterNextSearchCriteria(
    filterPill: AppliedFiltersType,
    sortOption: FlightsSortOptionTypeInput
  ) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);
    const nextFilterCriteria = toJS(filterPill.nextSearchSortFilterCriteria.filterCriteria);
    const sanitizedNextFilterCriteria = sanitizeFilterValues(nextFilterCriteria);

    searchCriteriaFeedback.searchFilterValuesList = sanitizedNextFilterCriteria;
    searchCriteriaFeedback.sortOption = sortOption;
    this.searchCriteria = searchCriteriaFeedback;
  }

  public syncWithSearchResults(searchResults: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch) {
    const searchCriteria = this.getSearchCriteriaFromSearchResults(searchResults);
    searchCriteria.queryState = FlightsQueryState.LOADED;
    searchCriteria.flightsSearchContext.journeysContinuationId =
      this.searchCriteria.flightsSearchContext.journeysContinuationId || null;
    this.forceFetch = false;
    this.hydrate({ searchCriteria });
  }

  public syncWithSearchFormStore(searchFormStore: SearchFormStore) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);

    Object.assign(searchCriteriaFeedback, this.getFlightsSearchContextFromSearchForm(searchFormStore.searchForm));
    Object.assign(searchCriteriaFeedback, this.getJourneyCriteriaFromSearchForm(searchFormStore.searchForm));
    Object.assign(searchCriteriaFeedback, this.getSearchPreferencesFromSearchForm(searchFormStore.searchForm));
    Object.assign(searchCriteriaFeedback, this.getTravelerDetailsFromSearchForm(searchFormStore.searchForm));
    Object.assign(searchCriteriaFeedback, this.unsetPagination());

    this.searchCriteria = searchCriteriaFeedback;
  }

  public syncWithListingStore(listingsStore: ListingsStore) {
    const searchCriteriaFeedback = Object.assign({}, this.searchCriteria);

    Object.assign(searchCriteriaFeedback, this.getPaginationFromListingsStore(listingsStore.listingsAction));

    this.searchCriteria = searchCriteriaFeedback;
  }

  public syncPreviousFlightSelections(previousFlightSelections: FlightsStandardOffer.PreviousFlightSelections[]) {
    if (previousFlightSelections) {
      this.searchCriteria.flightsSearchContext.previousFlightSelections = previousFlightSelections;
    }
  }

  public getSearchCriteriaFromSearchResults(
    searchResults: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch
  ) {
    const searchCriteriaFeedback = Object.assign(
      {},
      SearchCriteriaStore.DEFAULTS.searchCriteria
    ) as FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;

    Object.assign(searchCriteriaFeedback, this.getFlightsSearchContextFromSearchForm(searchResults.searchForm));
    Object.assign(searchCriteriaFeedback, this.getJourneyCriteriaFromSearchForm(searchResults.searchForm));
    Object.assign(searchCriteriaFeedback, this.getSearchPreferencesFromSearchForm(searchResults.searchForm));
    Object.assign(searchCriteriaFeedback, this.getTravelerDetailsFromSearchForm(searchResults.searchForm));
    Object.assign(searchCriteriaFeedback, this.getFiltersAppliedFromSpinnerResponse(searchResults.sortAndFilterResult));
    Object.assign(searchCriteriaFeedback, this.getSortOptionFromSortAndFilterResult(searchResults.sortAndFilterResult));

    return searchCriteriaFeedback;
  }

  public getFlightsSearchContextFromSearchForm(searchForm: FlightsSearchFormType) {
    const items = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormTripTypeOption'
    ).items as FlightsSearchFormTripTypeOptionItem[];
    const { tripType } = items.find(tripTypeOption => tripTypeOption.selected);

    return {
      flightsSearchContext: {
        tripType
      }
    };
  }

  public getJourneyCriteriaFromSearchForm(searchForm: FlightsSearchFormType) {
    const journeyCriteria = searchForm.journeys.reduce(this.mapSearchFormJourneyToInputJourney, []);

    return {
      journeyCriteria
    };
  }

  public mapSearchFormJourneyToInputJourney(
    journeyCriteria: FlightsJourneyCriteriaInput[],
    flightsSearchFormJourneyCriteria: FlightsSearchFormJourneyCriteriaType
  ): FlightsJourneyCriteriaInput[] {
    const departureDate = flightsSearchFormJourneyCriteria.departureDate.date;
    const returnDate = flightsSearchFormJourneyCriteria.returnDate && flightsSearchFormJourneyCriteria.returnDate.date;
    const { originLocation, destinationLocation } = flightsSearchFormJourneyCriteria;
    const inputJourney = {
      departureDate: {
        day: departureDate.day,
        month: departureDate.month,
        year: departureDate.year
      },
      destination: destinationLocation.value,
      origin: originLocation.value
    };

    journeyCriteria.push(inputJourney);

    if (returnDate) {
      journeyCriteria.push({
        departureDate: {
          day: returnDate.day,
          month: returnDate.month,
          year: returnDate.year
        },
        destination: originLocation.value,
        origin: destinationLocation.value
      });
    }

    return journeyCriteria;
  }

  public getFiltersAppliedFromSpinnerResponse(
    sortAndFilterResult: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedSortAndFilterResultInlineFragment
  ) {
    const filtersPresentation = sortAndFilterResult.filterPresentation;

    if (!filtersPresentation || !filtersPresentation.options) {
      return {
        searchFilterValuesList: []
      };
    }

    const searchFilterValuesList = this.flattenFilterValueInputsFromFilterOptions(
      filtersPresentation.options.map(this.getFilterValuesInputFromFilterOption.bind(this))
    );

    return { searchFilterValuesList };
  }

  public getSearchFilterValuesListFromSortAndFilterResult(sortAndFilterResult: SortFilterStore) {
    if (sortAndFilterResult.filterPresentation.__typename === 'FlightsFilterLoadingPresentation') {
      return;
    }

    const searchFilterValuesList = this.flattenFilterValueInputsFromFilterOptions(
      sortAndFilterResult.filterPresentation.options.map(this.getFilterValuesInputFromFilterOption.bind(this))
    );

    return { searchFilterValuesList };
  }

  public getFilterValuesInputFromFilterOption(
    filterOption: FlightsFilterLoadedOptionType
  ): FlightsSearchFilterValuesInput[] {
    const flightsSearchFilterValues = (filterOption.items as FlightsFilterItem[])
      .filter(filterItem => filterItem.selected)
      .map(filterItem => filterItem.flightsSearchFilterValues);

    return sanitizeFilterValues(flightsSearchFilterValues);
  }

  public flattenFilterValueInputsFromFilterOptions(filterValuesFromFilterOptions) {
    return filterValuesFromFilterOptions.reduce((filterValues, filterOptionInputs) => {
      filterOptionInputs.forEach(filterOptionInput => filterValues.push(filterOptionInput));

      return filterValues;
    }, []);
  }

  public getSearchPreferencesFromSearchForm(searchForm: FlightsSearchFormType) {
    const moreFiltersOption = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormMoreFiltersOption'
    ) as FlightsSearchFormMoreFiltersOption;
    const advancedFilters = moreFiltersOption.items
      .filter(moreFilterOption => moreFilterOption.selected)
      .map(moreFilterOption => moreFilterOption.advancedFilterType);

    const airlineOption = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormAirlineOption'
    ) as FlightsSearchFormAirlineOption;
    const airline = airlineOption.selectedItem.airline;

    const cabinClassOption = searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormCabinClassOption'
    ) as FlightsSearchFormCabinClassOption;
    const cabinClass = cabinClassOption.selectedItem.cabinClass;

    return {
      searchPreferences: {
        advancedFilters,
        airline,
        cabinClass
      }
    };
  }

  public getSortOptionFromSortAndFilterResult(
    sortAndFilterResult:
      | FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsLoadedSortAndFilterResultInlineFragment
      | SortFilterStore
  ) {
    return {
      sortOption: {
        ...sortAndFilterResult.sortPresentation.selectedOption.sortType
      }
    };
  }

  public unsetPagination() {
    return {
      searchPagination: null
    };
  }

  public getPaginationFromListingsStore(listingsAction: ListingsAction) {
    const searchPagination =
      listingsAction && listingsAction.searchPagination
        ? this.searchPaginationToSearchPaginationInput(listingsAction.searchPagination)
        : null;

    return {
      searchPagination
    };
  }

  public searchPaginationToSearchPaginationInput(searchPagination?: SearchPagination) {
    return {
      size: searchPagination.size,
      startingIndex: searchPagination.startingIndex
    };
  }

  public getTravelerDetailsFromSearchForm(searchForm: FlightsSearchFormType) {
    const travelerItems = (searchForm.searchOptions.find(
      searchOption => searchOption.__typename === 'FlightsSearchFormTravelersOption'
    ) as FlightsSearchFormTravelersOption).items;
    const travelerDetails = travelerItems
      .filter(item => item.travelerCount !== 0)
      .map(this.travelerOptionToTravelerDetailsInput.bind(this));

    return {
      travelerDetails
    };
  }

  public travelerOptionToTravelerDetailsInput(travelerOptionItem: TravelerOptionItem) {
    return {
      ages: travelerOptionItem.travelerAges && this.getTravelerAgesFromSelectors(travelerOptionItem.travelerAges),
      count: travelerOptionItem.travelerCount,
      travelerType: travelerOptionItem.type
    };
  }

  public getTravelerAgesFromSelectors(travelerAges: FlightsTravelerAgeSelector[]) {
    return travelerAges.reduce((ages, travelerAgeSelector) => {
      ages.push(travelerAgeSelector.items.find(item => item.selected).value);

      return ages;
    }, []);
  }

  public clearSortFilterSearchCriteria(): void {
    this.searchCriteria.searchFilterValuesList = [];
    this.searchCriteria.sortOption = null;
  }

  public clearJourneysContinuationId(): void {
    delete this.searchCriteria.flightsSearchContext.journeysContinuationId;
  }

  public clearPagination(): void {
    this.searchCriteria.searchPagination = null;
  }
}

SearchCriteriaStore.DEFAULTS = {
  searchCriteria: {
    flightsSearchContext: {
      tripType: FlightsTripType.ONE_WAY
    },
    journeyCriteria: [
      {
        departureDate: {
          day: 1,
          month: 1,
          year: 2020
        },
        destination: 'JFK',
        origin: 'SEA'
      }
    ],
    queryState: FlightsQueryState.LOADING,
    searchFilterValuesList: [],
    searchPreferences: {
      advancedFilters: [],
      airline: null,
      cabinClass: FlightsCabinClass.COACH
    },
    sortOption: null,
    travelerDetails: [
      {
        count: 1,
        travelerType: FlightsTravelerType.ADULT
      }
    ],
    searchPagination: null
  },
  flexSearchCriteria: {
    flightsSearchContext: {
      tripType: FlightsTripType.ONE_WAY
    },
    journeyCriteria: [
      {
        departureDate: {
          day: 1,
          month: 1,
          year: 2020
        },
        destination: 'JFK',
        origin: 'SEA'
      }
    ],
    queryState: FlightsQueryState.LOADING,
    searchPreferences: {
      advancedFilters: [],
      airline: null,
      cabinClass: FlightsCabinClass.COACH
    },
    travelerDetails: [
      {
        count: 1,
        travelerType: FlightsTravelerType.ADULT
      }
    ]
  },
  forceFetch: false,
  fetchFlexResults: true
};
